// ============================================================================
// useDashboardData — assembles the dashboard view-model from the real API,
// replacing src/mock/selectors. Pulls:
//   • the trainer-wide calendar (GET /api/calendar) -> sessions
//   • the client roster (GET /api/clients) -> clients + KPIs + roster
//   • per-client metrics (GET /api/clients/{id}/metrics) -> progress snapshot
// and derives KPIs / today / upcoming / roster the same way the old selectors
// did, so the locked Pulse components render identically.
// ============================================================================
import { useMemo } from "react";
import { useClients } from "../hooks/clients";
import { useCalendar } from "../hooks/sessions";
import { useActivityCalendar } from "../hooks/activities";
import { useMealCalendar } from "../hooks/nutrition";
import { useWeeklyGoalCalendar } from "../hooks/weeklyGoals";
import { startOfDay, startOfWeek } from "../lib/date";
import type {
  CalendarSession,
  Client,
} from "../api";
import {
  activityToVM,
  mealMarkerToVM,
  toAttendance,
  toLocalDateTime,
  toSessionType,
  weeklyGoalBannerToVM,
  type ActivityVM,
  type ClientVM,
  type DashboardKpisVM,
  type MealMarkerVM,
  type RosterEntryVM,
  type SessionVM,
  type WeeklyGoalBannerVM,
} from "./viewModel";

const FOLLOW_UP_DAYS = 7;

function clientToVM(c: Client): ClientVM {
  return {
    id: c.id,
    name: c.name,
    starred: !!c.starred,
    occupation: c.occupation ?? undefined,
    // Trend/goal derive from metrics; until a client is opened we show neutral.
    trend: "flat",
    trendLabel: "",
  };
}

function sessionToVM(s: CalendarSession): SessionVM {
  const startsAt = toLocalDateTime(s.performed_at ?? s.scheduled_at);
  return {
    id: s.id,
    clientId: s.client_id,
    clientName: s.client_name,
    status: s.status,
    startsAt,
    durationMin: s.duration_min ?? 0,
    type: toSessionType(s.type),
    rawType: s.type ?? undefined,
    location: s.location_text ?? undefined,
    locationId: s.location_id ?? undefined,
    feeAmount: s.fee_amount ?? undefined,
    currency: s.currency ?? undefined,
    paid: !!s.paid,
    sessionRpe: s.session_rpe ?? undefined,
    notes: s.notes ?? undefined,
    attendance: toAttendance(s.attendance_status),
    checkedInAt: s.checked_in_at ?? undefined,
  };
}

export interface DashboardData {
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  clients: ClientVM[];
  sessions: SessionVM[];
  /** Provenance-tagged activities (own/suggested) merged onto the calendar grid. */
  activities: ActivityVM[];
  /** Per-day amber meal-summary markers (one per client+day) merged onto the grid. */
  mealMarkers: MealMarkerVM[];
  /** Week-level lime weekly-goal banners (one per client+week) merged onto the grid. */
  weeklyGoals: WeeklyGoalBannerVM[];
  kpis: DashboardKpisVM;
  roster: RosterEntryVM[];
}

const EMPTY_KPIS: DashboardKpisVM = {
  activeClients: 0,
  sessionsThisWeek: 0,
  hoursThisWeek: 0,
  dueToSchedule: 0,
  deltas: {
    activeClients: "",
    sessionsThisWeek: "",
    hoursThisWeek: "",
    dueToSchedule: "all booked",
  },
};

export function useDashboardData(): DashboardData {
  const clientsQuery = useClients();
  // Open calendar feed: every session for this trainer (we bucket client-side).
  const calendarQuery = useCalendar();
  // Provenance-tagged activities (own + suggested) across every client, merged
  // onto the same grid (GET /api/activities/calendar — trainer-scoped).
  const activityCalendarQuery = useActivityCalendar();
  // Per-day meal-summary markers (one per client+day) across every client, the
  // amber meal-dots layer of the merge (GET /api/meals/calendar — trainer-scoped).
  const mealCalendarQuery = useMealCalendar();
  // Week-level weekly-goal banners (one per client+week) across every client, the
  // lime banner layer of the merge (GET /api/weekly-goals/calendar — trainer-scoped).
  const weeklyGoalCalendarQuery = useWeeklyGoalCalendar();

  return useMemo<DashboardData>(() => {
    const clientRows = clientsQuery.data ?? [];
    const sessionRows = calendarQuery.data ?? [];
    const activityRows = activityCalendarQuery.data ?? [];
    const mealRows = mealCalendarQuery.data ?? [];
    const weeklyGoalRows = weeklyGoalCalendarQuery.data ?? [];

    const clients = clientRows.map(clientToVM);
    const sessions = sessionRows
      .map(sessionToVM)
      .filter((s) => s.startsAt)
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    const activities = activityRows
      .map(activityToVM)
      .filter((a) => a.startsAt)
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    const mealMarkers = mealRows
      .map(mealMarkerToVM)
      .filter((m) => m.startsAt)
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
    const weeklyGoals = weeklyGoalRows
      .map(weeklyGoalBannerToVM)
      .filter((g) => g.weekStart)
      .sort((a, b) => a.weekStart.localeCompare(b.weekStart));

    const now = new Date();
    const today0 = startOfDay(now);
    const weekStart = startOfWeek(now);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    // --- KPIs ---
    const thisWeek = sessions.filter((s) => {
      const t = new Date(s.startsAt).getTime();
      return t >= weekStart.getTime() && t < weekEnd.getTime();
    });
    const hours = thisWeek.reduce((sum, s) => sum + s.durationMin, 0) / 60;

    const upcomingClientIds = new Set(
      sessions
        .filter(
          (s) =>
            s.status === "planned" &&
            new Date(s.startsAt).getTime() >= today0.getTime(),
        )
        .map((s) => s.clientId),
    );
    const dueToSchedule = clients.filter(
      (c) => !upcomingClientIds.has(c.id),
    ).length;

    const kpis: DashboardKpisVM = {
      activeClients: clients.length,
      sessionsThisWeek: thisWeek.length,
      hoursThisWeek: Math.round(hours * 10) / 10,
      dueToSchedule,
      deltas: {
        activeClients: "",
        sessionsThisWeek: "",
        hoursThisWeek: "",
        dueToSchedule: dueToSchedule > 0 ? "needs action" : "all booked",
      },
    };

    // --- Roster (last completed session per client + follow-up flag) ---
    const lastByClient = new Map<number, SessionVM>();
    for (const s of sessions) {
      if (s.status !== "completed") continue;
      const prev = lastByClient.get(s.clientId);
      if (!prev || s.startsAt > prev.startsAt) lastByClient.set(s.clientId, s);
    }

    const roster: RosterEntryVM[] = clients.map((client) => {
      const ls = lastByClient.get(client.id);
      let daysSinceLast: number | undefined;
      if (ls) {
        const diff =
          today0.getTime() - startOfDay(new Date(ls.startsAt)).getTime();
        daysSinceLast = Math.round(diff / 86_400_000);
      }
      const stale =
        daysSinceLast === undefined || daysSinceLast > FOLLOW_UP_DAYS;
      const needsFollowUp = !upcomingClientIds.has(client.id) && stale;
      return { client, lastSession: ls, daysSinceLast, needsFollowUp };
    });

    return {
      isLoading:
        clientsQuery.isLoading ||
        calendarQuery.isLoading ||
        activityCalendarQuery.isLoading ||
        mealCalendarQuery.isLoading ||
        weeklyGoalCalendarQuery.isLoading,
      isError:
        clientsQuery.isError ||
        calendarQuery.isError ||
        activityCalendarQuery.isError ||
        mealCalendarQuery.isError ||
        weeklyGoalCalendarQuery.isError,
      refetch: () => {
        void clientsQuery.refetch();
        void calendarQuery.refetch();
        void activityCalendarQuery.refetch();
        void mealCalendarQuery.refetch();
        void weeklyGoalCalendarQuery.refetch();
      },
      clients,
      sessions,
      activities,
      mealMarkers,
      weeklyGoals,
      kpis: clients.length === 0 && sessions.length === 0 ? EMPTY_KPIS : kpis,
      roster,
    };
  }, [
    clientsQuery.data,
    clientsQuery.isLoading,
    clientsQuery.isError,
    calendarQuery.data,
    calendarQuery.isLoading,
    calendarQuery.isError,
    activityCalendarQuery.data,
    activityCalendarQuery.isLoading,
    activityCalendarQuery.isError,
    mealCalendarQuery.data,
    mealCalendarQuery.isLoading,
    mealCalendarQuery.isError,
    weeklyGoalCalendarQuery.data,
    weeklyGoalCalendarQuery.isLoading,
    weeklyGoalCalendarQuery.isError,
    clientsQuery,
    calendarQuery,
    activityCalendarQuery,
    mealCalendarQuery,
    weeklyGoalCalendarQuery,
  ]);
}

// Type re-exports for components.
export type {
  ActivityVM,
  MealMarkerVM,
  WeeklyGoalBannerVM,
  ClientVM,
  SessionVM,
  DashboardKpisVM,
  RosterEntryVM,
  Trend,
  SessionType,
  EntryProvenance,
} from "./viewModel";
